import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CompanySelect from "@/components/CompanySelect";
import { useAuth } from "@/context/AuthContext";
import { api, pretty, TEMPLATE_CATEGORIES, isStaffRole } from "@/lib/api";

const Ring = ({ pct }) => {
  const r = 52, c = 2 * Math.PI * r;
  return (
    <div className="relative h-36 w-36 shrink-0" data-testid="progress-ring">
      <svg viewBox="0 0 128 128" className="h-full w-full">
        <circle cx="64" cy="64" r={r} fill="none" strokeWidth="12" className="stroke-muted" />
        <circle cx="64" cy="64" r={r} fill="none" strokeWidth="12" strokeLinecap="round" className="stroke-emerald-600 transition-[stroke-dashoffset] duration-700"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} transform="rotate(-90 64 64)" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-2xl font-extrabold" data-testid="progress-pct">{pct}%</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">verified</span>
      </div>
    </div>
  );
};

const FIGS = [
  ["total", "Active tasks", "", "text-foreground"],
  ["verified", "Verified", "status=verified", "text-emerald-700"],
  ["in_progress", "In progress", "status=in_progress,awaiting_client,returned", "text-blue-700"],
  ["in_review", "Submitted / review", "status=submitted,under_review", "text-sky-700"],
  ["pending", "Pending", "status=not_started", "text-slate-600"],
  ["unassigned", "Unassigned", "unassigned=true", "text-amber-700"],
  ["overdue", "Overdue", "overdue=true", "text-red-600"],
];

export default function ProgressPanel() {
  const { user, isStaff } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ date_from: "", date_to: "", tenant_id: "", assignee_id: "", category: "" });
  const [d, setD] = useState(null);
  const [people, setPeople] = useState([]);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  useEffect(() => { api.get("/dashboard/progress", { params }).then((r) => setD(r.data)); }, [JSON.stringify(f)]); // eslint-disable-line
  useEffect(() => { if (user.role !== "client_contributor") api.get("/users").then((r) => setPeople(r.data.filter((u) => u.status === "active" && (isStaff ? isStaffRole(u.role) : true)))).catch(() => {}); }, [isStaff, user.role]);
  const open = (q) => {
    const p = new URLSearchParams(q);
    Object.entries(params).forEach(([k, v]) => p.set(k, v));
    navigate(`/tasks?${p.toString()}`);
  };
  const set = (k) => (v) => setF({ ...f, [k]: v === "all" ? "" : v });
  return (
    <div className="rounded-lg border bg-card p-5" data-testid="progress-panel">
      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Task progress</h3>
        <div className="flex flex-wrap gap-2">
          <Input type="date" className="h-9 w-36" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} data-testid="progress-date-from" />
          <Input type="date" className="h-9 w-36" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} data-testid="progress-date-to" />
          {isStaff && <CompanySelect value={f.tenant_id} onChange={set("tenant_id")} allowAll testId="progress-company-filter" />}
          {user.role !== "client_contributor" && (
            <Select value={f.assignee_id || "all"} onValueChange={set("assignee_id")}>
              <SelectTrigger className="h-9 w-40" data-testid="progress-assignee-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All assignees</SelectItem><SelectItem value="me">Assigned to me</SelectItem>{people.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={f.category || "all"} onValueChange={set("category")}>
            <SelectTrigger className="h-9 w-36" data-testid="progress-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{TEMPLATE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{pretty(c)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {d && (
        <div className="flex flex-col items-center gap-6 sm:flex-row">
          <Ring pct={d.completion_pct} />
          <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-4">
            {FIGS.map(([k, label, q, cls]) => (
              <button key={k} onClick={() => open(q)} data-testid={`progress-fig-${k}`} className="rounded-md border p-3 text-left transition-colors hover:border-primary/50 hover:bg-muted/40">
                <div className={`font-heading text-2xl font-extrabold ${cls}`}>{d[k]}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      <p className="mt-3 text-[11px] text-muted-foreground">Completion = verified ÷ active tasks. Cancelled tasks are excluded; submitted tasks are not counted as complete until verified.</p>
    </div>
  );
}
